import type { ReactNode } from 'react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Eyebrow } from '../../components/Eyebrow';
import type { SkuBenchmark, SkuUsage } from '../../data/analytics';
import type { ApiDetail, CategoryId } from '../../features/catalog/model';
import { PlatformMark } from '../../components/PlatformMark';
import { useCatalogLinks } from '../../components/CatalogLinks';
import { apiRequests, apiTotals, cheapest, mostPopular } from '../../lib/catalog';
import { aggregateBenchmarks, hasBenchmarkPopulation } from '../../lib/benchmarks';
import { usdCost } from '../../lib/format';
import { quotedRateLabel, quotedRateUsd } from '../../lib/pricing';
import { apiUsageSeries } from './apiUsageSeries';
import { MeasuredCards } from '../../components/MeasuredCards';
import { PageHeader } from '../../components/editorial/PageHeader';
import { m } from '../../paraglide/messages';

/** One fact in the header strip: quiet label, loud value, optional unit. */
const Cell = ({ k, v, unit }: { k: string; v: ReactNode; unit?: string }) => (
  <span className="flex min-w-0 items-baseline gap-1.5">
    <span className="text-text-3">{k}</span>
    <b className="font-medium text-text">{v}</b>
    {unit ? <span className="truncate text-text-3">{unit}</span> : null}
  </span>
);

const Rule = () => (
  <span aria-hidden className="hidden h-3 w-px shrink-0 bg-line-2 sm:block" />
);

export interface ApiHeaderProps {
  api: ApiDetail;
  /** kebab-case HugeIcons name for the header chip */
  icon: string;
  /** real per-SKU traffic, keyed by slug */
  usage: Record<string, SkuUsage>;
  /** current-window measurements, keyed by slug */
  benchmarks: Record<string, SkuBenchmark>;
  /** in-page anchor of the full measured panel */
  panelHref: string;
}

/** API detail page header, as the page's opening `Band`: crumb, the platform's
 *  mark beside the h1, the blurb in full, a mono fact strip and the three
 *  measured cards. No category badge sits beside the h1 - nested in the heading
 *  it joined the accessible name with no separator, so every platform page
 *  announced itself as "InstagramSocial" and none of them contained the word
 *  "API". */
export const ApiHeader = ({ api, icon, usage, benchmarks, panelHref }: ApiHeaderProps) => {
  const links = useCatalogLinks();
  const totals = apiTotals(api);
  const popular = mostPopular(api, usage);
  const popularCheap = cheapest(popular);
  const skus = api.endpoints.map((e) => e.sku);
  const summary = aggregateBenchmarks(skus, benchmarks);
  const showUptime = summary
    ? hasBenchmarkPopulation(summary.uptimeSample)
    : false;
  const showLatency = summary
    ? hasBenchmarkPopulation(summary.latencySample)
    : false;

  return (
    <PageHeader
      eyebrow={<Eyebrow to={links.catalog}>{m.platform_catalog()}</Eyebrow>}
      title={
        <ParaglideMessage
          message={m.platform_header_title}
          inputs={{ name: api.name }}
          markup={{ accent: ({ children }) => <span className="muted-word">{children}</span> }}
        />
      }
      mark={
        <PlatformMark
          id={api.id}
          icon={icon}
          category={api.category as CategoryId}
          size={40}
          iconSize={22}
        />
      }
      lede={api.blurb}
    >
      <div className="mono flex flex-wrap items-center gap-x-4 gap-y-2 text-12">
        <Cell k={m.platform_header_endpoints()} v={totals.endpoints} />
        <Rule />
        <Cell k={m.platform_header_providers()} v={totals.providers} />
        <Rule />
        <Cell
          k={m.platform_header_from({ action: popular.action.toLowerCase() })}
          v={usdCost(quotedRateUsd(popularCheap.price))}
          unit={`${quotedRateLabel(popularCheap.price)} · ${popularCheap.source.name}`}
        />
        <span className="flex items-center gap-4 sm:ml-auto">
          <a href={panelHref} className="text-text-3 hover:text-accent">
            {m.platform_header_full_breakdown()}
          </a>
          <a href="https://getanyapi.com/docs" className="text-accent hover:underline">
            {m.platform_header_read_docs()}
          </a>
        </span>
      </div>

      <MeasuredCards
        className="mt-5"
        uptimePct={showUptime && summary ? summary.uptimePct : undefined}
        uptimeSample={summary?.uptimeSample}
        latencyP50Ms={showLatency && summary ? summary.latencyP50Ms : undefined}
        requests={apiRequests(api, usage)}
        weekly={apiUsageSeries(api, usage)}
      />
    </PageHeader>
  );
};
